<template>
  <LoadingView :loading="isLoading">
    <div class="flex items-center justify-between gap-6 mb-4">
      <UIHeading :title="'Расписание'" tag="3" />
      <UIButton
        type="gray"
        size="sm"
        :title="showMap ? 'Показать списком' : 'Показать на карте'"
        @click="showMap = !showMap"
      />
    </div>

    <InlineCalendar v-model="date" :dates="slots" />

    <SessionSchedule v-if="!showMap" :data="data" :url="url" />

    <YandexMap
      v-else
      :settings="{
        location: {
          center: [
            resources[0].address.latitude,
            resources[0].address.longitude,
          ].reverse(),
          zoom: 15,
        },
        theme,
      }"
      real-settings-location
      class="rounded-md overflow-hidden"
      :height="'500px'"
    >
      <YandexMapMarker
        v-for="item in resources"
        :key="item.id"
        :position="'left-center top'"
        :settings="{
          coordinates:
            [item.address.latitude, item.address.longitude].reverse() || [],
        }"
      >
        <button @click="callModal(item.id)">
          <IconMapPinFilled class="size-10 text-primary" />
        </button>
      </YandexMapMarker>

      <YandexMapDefaultFeaturesLayer />
      <YandexMapDefaultSchemeLayer />
    </YandexMap>
  </LoadingView>
</template>

<script setup>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { cloneDeep } from 'lodash-es'
import { h, inject, ref, watch } from 'vue'
import { useQuery } from 'vue-query'
import { useRoute } from 'vue-router'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapMarker,
} from 'vue-yandex-maps'

import { useGlobalStore } from '@/store/global.js'

import { IconMapPin, IconMapPinFilled } from '@tabler/icons-vue'

import SessionSchedule from '@/components/common/SessionSchedule.vue'
import InlineCalendar from '@/components/formkit/InlineCalendar.vue'
import LoadingView from '@/components/loading/LoadingView.vue'
import VenuesModal from '@/components/modals/VenuesModal.vue'
import UIBadge from '@/components/ui/UIBadge.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import useModalDynamic from '@/plugins/useModalDynamic.js'
import {
  callExternalModal,
  callMapModal,
  getDateFormat,
  lzDecompress,
} from '@/utils/global.js'

const theme = inject('theme')
const props = defineProps({
  resources: {
    type: Array,
    default: [],
  },
  slots: {
    type: Array,
    default: [],
  },
  url: {
    type: String,
    default: null,
  },
})
const showMap = ref(false)
const route = useRoute()
const http = inject('http')
const global = useGlobalStore()

dayjs.extend(customParseFormat)

const filterDate = lzDecompress(global.resources?.filters)?.date
const date = ref(new Date())

function useVenuesQuery() {
  return useQuery(
    ['event-venues-modal', route.params.id],
    () =>
      http(`/events/${route.params.id}/venues`, {
        params: {
          date: date.value,
        },
      }).then(({ resources }) => {
        return resources
      }),
    {
      initialData: cloneDeep(props.resources),
      staleTime: 10000,
    },
  )
}

const { data, refetch, isLoading } = useVenuesQuery()

const prepareDates = props.slots.map((item) =>
  dayjs(item, 'DD.MM.YYYY').toDate(),
)

// Функция для получения массива месяцев
function getMonthsFromDates(dates) {
  // Создаем Set для хранения уникальных месяцев
  const monthsSet = new Set()

  // Перебираем массив дат и добавляем месяцы в Set
  dates.forEach((date) => {
    const month = dayjs(date).format('YYYY-MM') // Форматируем дату как 'YYYY-MM'
    monthsSet.add(month)
  })

  // Преобразуем Set обратно в массив
  return Array.from(monthsSet)
}

function callModal(id) {
  useModalDynamic({
    opened: true,
    component: VenuesModal,
    compAttrs: {
      data: data.value.filter((item) => item.id === id),
      url: props.url,
    },
  })
}

watch(
  () => date.value,
  () => {
    refetch.value()
  },
)
</script>
